import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Description as DocuSignIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as ViewedIcon,
  RateReview as ReviewIcon,
  PictureAsPdf as PdfIcon,
  Article as ClausesIcon,
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Contract, DocuSignStep } from '../types';
import {
  contractService,
  contractStatusColors,
  contractStatusLabels,
  contractTypeLabels,
} from '../services/contractService';
import { colors } from '../theme';

// ============================================================================
// Tab Panel Component
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ============================================================================
// DocuSign Step mapping
// ============================================================================

const docusignSteps: DocuSignStep[] = ['draft', 'sent', 'viewed', 'signed'];

const getDocuSignActiveStep = (step?: DocuSignStep): number => {
  if (!step) return 0;
  return docusignSteps.indexOf(step);
};

// ============================================================================
// Main Component
// ============================================================================

const ContractDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<Contract | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [sendReviewDialogOpen, setSendReviewDialogOpen] = useState(false);
  const [sendDocuSignDialogOpen, setSendDocuSignDialogOpen] = useState(false);
  const [simulateViewDialogOpen, setSimulateViewDialogOpen] = useState(false);
  const [simulateSignDialogOpen, setSimulateSignDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // =========================================================================
  // Load data
  // =========================================================================

  useEffect(() => {
    if (!id) {
      navigate('/contracts');
      return;
    }

    contractService.initializeIfEmpty();
    const found = contractService.getById(id);
    if (!found) {
      navigate('/contracts');
      return;
    }
    setContract(found);
    setLoading(false);

    const handleUpdate = () => {
      if (id) {
        const updated = contractService.getById(id);
        if (updated) setContract(updated);
      }
    };
    window.addEventListener('contractsUpdated', handleUpdate);
    return () => window.removeEventListener('contractsUpdated', handleUpdate);
  }, [id, navigate]);

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleSendReview = () => {
    if (!contract) return;
    contractService.sendToReview(contract.id);
    setContract(contractService.getById(contract.id));
    setSendReviewDialogOpen(false);
    setSnackbar({ open: true, message: 'Contrato enviado a revisión', severity: 'info' });
  };

  const handleSendDocuSign = () => {
    if (!contract) return;
    contractService.sendToDocuSign(contract.id);
    setContract(contractService.getById(contract.id));
    setSendDocuSignDialogOpen(false);
    setSnackbar({ open: true, message: `Contrato enviado a DocuSign para firma electrónica`, severity: 'success' });
    setTabValue(3); // Switch to DocuSign tab
  };

  const handleSimulateView = () => {
    if (!contract) return;
    contractService.simulateDocuSignView(contract.id);
    setContract(contractService.getById(contract.id));
    setSimulateViewDialogOpen(false);
    setSnackbar({ open: true, message: `${contract.supplierName} visualizó el documento`, severity: 'info' });
  };

  const handleSimulateSign = () => {
    if (!contract) return;
    contractService.simulateDocuSignSign(contract.id);
    setContract(contractService.getById(contract.id));
    setSimulateSignDialogOpen(false);
    setSnackbar({ open: true, message: `Contrato firmado electrónicamente por ${contract.supplierName}`, severity: 'success' });
  };

  const handleCancel = () => {
    if (!contract) return;
    contractService.cancel(contract.id, cancelReason || undefined);
    setContract(contractService.getById(contract.id));
    setCancelDialogOpen(false);
    setCancelReason('');
    setSnackbar({ open: true, message: 'Contrato cancelado', severity: 'info' });
  };

  const handleDelete = () => {
    if (!contract) return;
    contractService.delete(contract.id);
    setDeleteDialogOpen(false);
    navigate('/contracts');
  };

  // =========================================================================
  // Helpers
  // =========================================================================

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return '—';
    }
  };

  const formatDateShort = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return '—';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  // =========================================================================
  // Loading/not found
  // =========================================================================

  if (loading || !contract) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  const daysToExpiry = differenceInDays(parseISO(contract.endDate), new Date());
  const isExpiringSoon = contract.status === 'signed' && daysToExpiry >= 0 && daysToExpiry <= 30;

  return (
    <Box>
      {/* Breadcrumb + Header */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/contracts')}>
          Contratos LTP
        </Link>
        <Typography color="text.primary">{contract.contractNumber}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/contracts')}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {contract.contractNumber}
              </Typography>
              <Chip
                label={contractStatusLabels[contract.status]}
                sx={{
                  bgcolor: contractStatusColors[contract.status],
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              {contract.status === 'sent_to_sign' && contract.docusignEnvelopeId && (
                <Chip
                  label="DocuSign"
                  size="small"
                  variant="outlined"
                  sx={{ color: colors.info.main, borderColor: colors.info.main }}
                />
              )}
            </Box>
            <Typography variant="body1" color="text.secondary">
              {contract.title}
            </Typography>
          </div>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {contract.status === 'draft' && (
            <>
              <Button
                variant="contained"
                startIcon={<ReviewIcon />}
                onClick={() => setSendReviewDialogOpen(true)}
                sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
              >
                Enviar a Revisión
              </Button>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Eliminar
              </Button>
            </>
          )}
          {contract.status === 'review' && (
            <>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setSendDocuSignDialogOpen(true)}
                sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
              >
                Enviar a DocuSign
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancelar
              </Button>
            </>
          )}
          {contract.status === 'sent_to_sign' && (
            <>
              {contract.docusignStep === 'sent' && (
                <Button
                  variant="outlined"
                  startIcon={<ViewedIcon />}
                  onClick={() => setSimulateViewDialogOpen(true)}
                >
                  Simular: Proveedor Vio
                </Button>
              )}
              {(contract.docusignStep === 'sent' || contract.docusignStep === 'viewed') && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setSimulateSignDialogOpen(true)}
                  sx={{ bgcolor: colors.success.main, '&:hover': { bgcolor: colors.success.dark } }}
                >
                  Simular: Firma
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancelar
              </Button>
            </>
          )}
          {contract.status === 'signed' && (
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={() => setSnackbar({ open: true, message: 'Descarga de PDF (mock) — En producción se generaría el documento completo', severity: 'info' })}
            >
              Descargar PDF
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="Información" />
          <Tab icon={<ClausesIcon />} iconPosition="start" label={`Cláusulas (${contract.clauses.length})`} />
          <Tab icon={<HistoryIcon />} iconPosition="start" label={`Historial (${contract.history.length})`} />
          <Tab icon={<DocuSignIcon />} iconPosition="start" label="DocuSign" />
        </Tabs>

        {/* Tab 1: Información General */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3, pt: 0 }}>
            <Grid container spacing={3}>
              {/* Datos del contrato */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Datos del Contrato
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Número:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" fontWeight={600}>{contract.contractNumber}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Título:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{contract.title}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Tipo:</Typography></Grid>
                      <Grid item xs={8}><Chip label={contractTypeLabels[contract.type]} size="small" variant="outlined" /></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Valor:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" fontWeight={700} sx={{ fontSize: '1.1rem' }}>{formatCurrency(contract.value)}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Moneda:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{contract.currency}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Inicio:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{formatDateShort(contract.startDate)}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Fin:</Typography></Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isExpiringSoon ? 600 : 400,
                            color: isExpiringSoon ? colors.warning.dark : contract.status === 'expired' ? colors.error.main : 'text.primary',
                          }}
                        >
                          {formatDateShort(contract.endDate)}
                          {isExpiringSoon && (
                            <Chip label={`Vence en ${daysToExpiry}d`} size="small" sx={{ ml: 1, bgcolor: colors.warning.main, color: 'white', fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Creado:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{formatDate(contract.createdAt)}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Creado por:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{contract.createdBy}</Typography></Grid>

                      {contract.signedAt && (
                        <>
                          <Grid item xs={4}><Typography variant="body2" color="text.secondary">Firmado:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2" sx={{ color: colors.success.main, fontWeight: 600 }}>{formatDate(contract.signedAt)}</Typography></Grid>
                        </>
                      )}

                      {contract.notes && (
                        <>
                          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                          <Grid item xs={4}><Typography variant="body2" color="text.secondary">Notas:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2" sx={{ fontStyle: 'italic' }}>{contract.notes}</Typography></Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Datos del proveedor + Términos */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Proveedor
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>{contract.supplierName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      ID: {contract.supplierId}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Términos ({contract.terms.length})
                    </Typography>
                    {contract.terms.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Sin términos definidos</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Descripción</TableCell>
                              <TableCell align="right">Valor</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contract.terms.map((term) => (
                              <TableRow key={term.id}>
                                <TableCell><Typography variant="body2">{term.description}</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2" fontWeight={600}>{term.value}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Cláusulas */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3, pt: 0 }}>
            {contract.clauses.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: '#fafafa' }}>
                <ClausesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  No hay cláusulas definidas
                </Typography>
              </Paper>
            ) : (
              contract.clauses.map((clause, i) => (
                <Paper key={clause.id} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={i + 1}
                      size="small"
                      sx={{ bgcolor: colors.brand.blue, color: 'white', fontWeight: 700, minWidth: 28 }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {clause.title}
                    </Typography>
                    {clause.required && (
                      <Chip label="Obligatoria" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: colors.brand.blue, borderColor: colors.brand.blue }} />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 5 }}>
                    {clause.content}
                  </Typography>
                </Paper>
              ))
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Historial */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3, pt: 0 }}>
            {contract.history.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: '#fafafa' }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Sin historial de eventos
                </Typography>
              </Paper>
            ) : (
              <Stepper orientation="vertical" activeStep={contract.history.length - 1}>
                {contract.history.map((entry) => (
                  <Step key={entry.id} completed={true}>
                    <StepLabel
                      optional={
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.date)} — {entry.user}
                        </Typography>
                      }
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {entry.action}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {entry.details && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {entry.details}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>
        </TabPanel>

        {/* Tab 4: DocuSign */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3, pt: 0 }}>
            {!contract.docusignEnvelopeId ? (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: '#fafafa' }}>
                <DocuSignIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  No enviado a DocuSign
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  {contract.status === 'draft'
                    ? 'Envíe el contrato a revisión primero, luego podrá enviarlo a DocuSign'
                    : contract.status === 'review'
                      ? 'El contrato está en revisión. Una vez aprobado, podrá enviarlo a DocuSign para firma.'
                      : 'Este contrato no fue enviado a firma electrónica.'}
                </Typography>
                {contract.status === 'review' && (
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    sx={{ mt: 3, bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
                    onClick={() => setSendDocuSignDialogOpen(true)}
                  >
                    Enviar a DocuSign
                  </Button>
                )}
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {/* DocuSign Status Card */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Estado DocuSign
                      </Typography>

                      <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        <Grid item xs={5}><Typography variant="body2" color="text.secondary">Envelope ID:</Typography></Grid>
                        <Grid item xs={7}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {contract.docusignEnvelopeId}
                          </Typography>
                        </Grid>

                        {contract.docusignSentAt && (
                          <>
                            <Grid item xs={5}><Typography variant="body2" color="text.secondary">Enviado:</Typography></Grid>
                            <Grid item xs={7}><Typography variant="body2">{formatDate(contract.docusignSentAt)}</Typography></Grid>
                          </>
                        )}
                        {contract.docusignViewedAt && (
                          <>
                            <Grid item xs={5}><Typography variant="body2" color="text.secondary">Visto:</Typography></Grid>
                            <Grid item xs={7}><Typography variant="body2">{formatDate(contract.docusignViewedAt)}</Typography></Grid>
                          </>
                        )}
                        {contract.docusignSignedAt && (
                          <>
                            <Grid item xs={5}><Typography variant="body2" color="text.secondary">Firmado:</Typography></Grid>
                            <Grid item xs={7}><Typography variant="body2" sx={{ color: colors.success.main, fontWeight: 600 }}>{formatDate(contract.docusignSignedAt)}</Typography></Grid>
                          </>
                        )}
                      </Grid>

                      {/* Simulation buttons */}
                      {contract.status === 'sent_to_sign' && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Simulación DocuSign (solo demo)
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {contract.docusignStep === 'sent' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ViewedIcon />}
                                onClick={() => setSimulateViewDialogOpen(true)}
                              >
                                Simular: Vista
                              </Button>
                            )}
                            {(contract.docusignStep === 'sent' || contract.docusignStep === 'viewed') && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => setSimulateSignDialogOpen(true)}
                                sx={{ bgcolor: colors.success.main, '&:hover': { bgcolor: colors.success.dark } }}
                              >
                                Simular: Firma
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* DocuSign Stepper */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Progreso de Firma
                      </Typography>
                      <Stepper activeStep={getDocuSignActiveStep(contract.docusignStep)} orientation="vertical">
                        <Step completed={getDocuSignActiveStep(contract.docusignStep) >= 0}>
                          <StepLabel
                            StepIconProps={{
                              sx: { color: getDocuSignActiveStep(contract.docusignStep) >= 0 ? colors.info.main : undefined },
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>Borrador</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Contrato preparado y listo para envío
                            </Typography>
                          </StepLabel>
                        </Step>
                        <Step completed={getDocuSignActiveStep(contract.docusignStep) >= 1}>
                          <StepLabel>
                            <Typography variant="subtitle2" fontWeight={600}>Enviado</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {contract.docusignSentAt
                                ? `Enviado a ${contract.supplierName} el ${formatDateShort(contract.docusignSentAt)}`
                                : `Pendiente de envío a ${contract.supplierName}`}
                            </Typography>
                          </StepLabel>
                        </Step>
                        <Step completed={getDocuSignActiveStep(contract.docusignStep) >= 2}>
                          <StepLabel>
                            <Typography variant="subtitle2" fontWeight={600}>Visto</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {contract.docusignViewedAt
                                ? `El proveedor abrió el documento el ${formatDateShort(contract.docusignViewedAt)}`
                                : 'El proveedor aún no abrió el documento'}
                            </Typography>
                          </StepLabel>
                        </Step>
                        <Step completed={getDocuSignActiveStep(contract.docusignStep) >= 3}>
                          <StepLabel>
                            <Typography variant="subtitle2" fontWeight={600}>Firmado</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {contract.docusignSignedAt
                                ? `Firma electrónica completada el ${formatDateShort(contract.docusignSignedAt)}`
                                : 'Pendiente de firma electrónica'}
                            </Typography>
                          </StepLabel>
                        </Step>
                      </Stepper>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* ================================================================= */}
      {/* Dialogs                                                           */}
      {/* ================================================================= */}

      {/* Send to Review */}
      <Dialog open={sendReviewDialogOpen} onClose={() => setSendReviewDialogOpen(false)}>
        <DialogTitle>Enviar a Revisión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Enviar el contrato <strong>{contract.contractNumber}</strong> a revisión interna?
            Una vez aprobado, podrá enviarse a DocuSign para firma electrónica.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendReviewDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSendReview}
            variant="contained"
            startIcon={<ReviewIcon />}
            sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
          >
            Enviar a Revisión
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send to DocuSign */}
      <Dialog open={sendDocuSignDialogOpen} onClose={() => setSendDocuSignDialogOpen(false)}>
        <DialogTitle>Enviar a DocuSign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Enviar el contrato <strong>{contract.contractNumber}</strong> a <strong>{contract.supplierName}</strong> para firma electrónica vía DocuSign?
            <br /><br />
            Se generará un envelope y el proveedor recibirá un email con el documento para firmar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDocuSignDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSendDocuSign}
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
          >
            Enviar a DocuSign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simulate View */}
      <Dialog open={simulateViewDialogOpen} onClose={() => setSimulateViewDialogOpen(false)}>
        <DialogTitle>Simular: Proveedor Vio el Documento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Simular que <strong>{contract.supplierName}</strong> abrió el documento en DocuSign.
            <br /><br />
            <em>En producción, esto sucede automáticamente vía webhook de DocuSign.</em>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulateViewDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSimulateView} variant="outlined" startIcon={<ViewedIcon />}>
            Simular Vista
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simulate Sign */}
      <Dialog open={simulateSignDialogOpen} onClose={() => setSimulateSignDialogOpen(false)}>
        <DialogTitle>Simular: Firma Electrónica</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Simular que <strong>{contract.supplierName}</strong> firmó el contrato electrónicamente en DocuSign.
            <br /><br />
            El contrato pasará a estado <strong>Firmado</strong> y quedará vigente.
            <br /><br />
            <em>En producción, esto sucede automáticamente vía webhook de DocuSign.</em>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulateSignDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSimulateSign}
            variant="contained"
            startIcon={<CheckCircleIcon />}
            sx={{ bgcolor: colors.success.main, '&:hover': { bgcolor: colors.success.dark } }}
          >
            Simular Firma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancelar Contrato</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Cancelar el contrato <strong>{contract.contractNumber}</strong> — "{contract.title}"?
          </DialogContentText>
          <TextField
            fullWidth
            label="Motivo de cancelación (opcional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Volver</Button>
          <Button onClick={handleCancel} color="error" variant="contained" startIcon={<CancelIcon />}>
            Cancelar Contrato
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Contrato</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar el contrato <strong>{contract.contractNumber}</strong> — "{contract.title}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractDetail;
